import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import PageTransition from "@/components/layout/PageTransition";
import PageHero from "@/components/common/PageHero";
import AnimatedSection from "@/components/common/AnimatedSection";
import { api } from "@/lib/api";

export default function BlogDetailPage() {
  const { blogId } = useParams();
  const [blog, setBlog] = useState(null);

  useEffect(() => {
    const loadBlog = async () => {
      try {
        const response = await api.get(`/public/blogs/${blogId}`);
        setBlog(response.data);
      } catch {
        toast.error("Unable to load this blog");
      }
    };

    loadBlog();
  }, [blogId]);

  if (!blog) {
    return (
      <PageTransition testId="blog-detail-page-loading">
        <main className="mx-auto max-w-4xl px-6 py-20 md:px-10" data-testid="blog-detail-loading-state">
          <p className="font-[Manrope] text-sm text-[#666666]" data-testid="blog-detail-loading-text">
            Loading blog article...
          </p>
        </main>
      </PageTransition>
    );
  }

  return (
    <PageTransition testId="blog-detail-page">
      <PageHero
        title={blog.title}
        subtitle={`${blog.author} · ${blog.published_on}`}
        image={blog.image_url}
        testId="blog-detail-page-hero"
      />

      <main className="mx-auto max-w-5xl px-6 py-14 md:px-10 md:py-20" data-testid="blog-detail-main-content">
        <AnimatedSection testId="blog-detail-image-section">
          <div className="overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white">
            <img
              src={blog.image_url}
              alt={blog.title}
              className="aspect-[16/8] w-full object-cover"
              data-testid="blog-detail-feature-image"
            />
          </div>
        </AnimatedSection>

        <AnimatedSection className="mt-8" testId="blog-detail-text-section">
          <article className="rounded-2xl border border-[#f2e6c9] bg-white p-7 md:p-10" data-testid="blog-detail-text-card">
            <p className="font-[Manrope] text-xs uppercase tracking-[0.16em] text-[#8b7a55]" data-testid="blog-detail-meta">
              {blog.author} · {blog.published_on}
            </p>
            <h1 className="mt-2 font-[Playfair_Display] text-4xl text-[#1a1a1a]" data-testid="blog-detail-title">
              {blog.title}
            </h1>
            <p className="mt-4 font-[Manrope] text-sm leading-relaxed text-[#555555]" data-testid="blog-detail-content">
              {blog.content}
            </p>
          </article>
        </AnimatedSection>

        <AnimatedSection className="mt-8" testId="blog-detail-back-link-section">
          <Link
            to="/blogs"
            className="inline-flex rounded-full border border-[#c5a059] px-5 py-2.5 font-[Manrope] text-xs font-semibold uppercase tracking-[0.15em] text-[#b08d45] hover:bg-[#f9f4e8]"
            data-testid="blog-detail-back-to-blogs-link"
          >
            Back to Blogs
          </Link>
        </AnimatedSection>
      </main>
    </PageTransition>
  );
}
